import { Routes } from '@angular/router';
import { BaseComponent } from './pages/base/base.component';
import { loggedInGuard } from './core/guards/logged-in/logged-in.guard';
import { loggedOutGuard } from './core/guards/logged-out/logged-out.guard';
import { FacultiesDetailsComponent } from './pages/faculties/faculties-details/faculties-details.component';
import { ProgramsDetailsComponent } from './pages/programs/programs-details/programs-details.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { DepartmentsDetailsComponent } from './pages/departments/departments-details/departments-details.component';

export const routes: Routes = [
  {
    path: '',
    component: BaseComponent,
    canActivate: [loggedInGuard],
    children: [
      {
        path: 'faculties',
        title: 'Faculties',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './pages/faculties/all-faculties/all-faculties.component'
              ).then((m) => m.AllFacultiesComponent),
          },
          {
            path: ':facultyID',
            component: FacultiesDetailsComponent,
            children: [
              {
                path: 'departments',
                loadComponent: () =>
                  import(
                    './pages/faculties/faculties-details/faculty-departments/faculty-departments.component'
                  ).then((m) => m.FacultyDepartmentsComponent),
              },
              {
                path: 'programs',
                loadComponent: () =>
                  import(
                    './pages/faculties/faculties-details/faculty-programs/faculty-programs.component'
                  ).then((m) => m.FacultyProgramsComponent),
              },
              {
                path: 'courses',
                loadComponent: () =>
                  import(
                    './pages/faculties/faculties-details/faculty-courses/faculty-courses.component'
                  ).then((m) => m.FacultyCoursesComponent),
              },
              {
                path: 'students',
                loadComponent: () =>
                  import(
                    './pages/faculties/faculties-details/faculty-students/faculty-students.component'
                  ).then((m) => m.FacultyStudentsComponent),
              },
              {
                path: 'instructors',
                loadComponent: () =>
                  import(
                    './pages/faculties/faculties-details/faculty-instructors/faculty-instructors.component'
                  ).then((m) => m.FacultyInstructorsComponent),
              },
            ],
          },
        ],
      },

      {
        path: 'programs',
        title: 'Programs',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './pages/programs/all-programs/all-programs.component'
              ).then((m) => m.AllProgramsComponent),
          },
          {
            path: ':programID',
            component: ProgramsDetailsComponent,
            children: [
              {
                path: 'courses',
                loadComponent: () =>
                  import(
                    './pages/programs/programs-details/program-courses/program-courses.component'
                  ).then((m) => m.ProgramCoursesComponent),
              },
              {
                path: 'students',
                loadComponent: () =>
                  import(
                    './pages/programs/programs-details/program-students/program-students.component'
                  ).then((m) => m.ProgramStudentsComponent),
              },
            ],
          },
        ],
      },
      {
        path: 'departments',
        title: 'Departments',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './pages/departments/all-departments/all-departments.component'
              ).then((m) => m.AllDepartmentsComponent),
          },
          {
            path: ':departmentID',
            component: DepartmentsDetailsComponent,
            children: [
              {
                path: 'programs',
                loadComponent: () =>
                  import(
                    './pages/departments/departments-details/department-programs/department-programs.component'
                  ).then((m) => m.DepartmentProgramsComponent),
              },
              {
                path: 'courses',
                loadComponent: () =>
                  import(
                    './pages/departments/departments-details/department-courses/department-courses.component'
                  ).then((m) => m.DepartmentCoursesComponent),
              },
              {
                path: 'instructors',
                loadComponent: () =>
                  import(
                    './pages/departments/departments-details/department-instructors/department-instructors.component'
                  ).then((m) => m.DepartmentInstructorsComponent),
              },
              {
                path: 'students',
                loadComponent: () =>
                  import(
                    './pages/departments/departments-details/department-students/department-students.component'
                  ).then((m) => m.DepartmentStudentsComponent),
              },
            ],
          },
        ],
      },
      {
        path: 'courses',
        title: 'Courses',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/courses/all-courses/all-courses.component').then(
                (m) => m.AllCoursesComponent
              ),
          },
        ],
      },
      {
        path: 'course-sections',
        title: 'Course Sections',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './pages/course-sections/all-course-sections/all-course-sections.component'
              ).then((m) => m.AllCourseSectionsComponent),
          },
          {
            path: ':courseSectionID/students',
            loadComponent: () =>
              import(
                './pages/students/course-section-students/course-section-students.component'
              ).then((m) => m.CourseSectionStudentsComponent),
          },
        ],
      },
      {
        path: 'instructors',
        title: 'Instructors',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './pages/instructors/all-instructors/all-instructors.component'
              ).then((m) => m.AllInstructorsComponent),
          },
        ],
      },
      {
        path: 'students',
        title: 'Students',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './pages/students/all-students/all-students.component'
              ).then((m) => m.AllStudentsComponent),
          },
        ],
      },
      {
        path: 'settings',
        title: 'Settings',
        children: [
          {
            path: '',
            component: SettingsComponent,
            children: [
              {
                path: 'change-password',
                loadComponent: () =>
                  import(
                    './pages/settings/change-password/change-password.component'
                  ).then((m) => m.ChangePasswordComponent),
              },
            ],
          },
        ],
      },
    ],
  },

  {
    path: 'auth',
    canActivate: [loggedOutGuard],
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./pages/auth/login/login.component').then(
            (m) => m.LoginComponent
          ),
      },
      {
        path: 'first-time-login',
        loadComponent: () =>
          import(
            './pages/auth/first-time-login/first-time-login.component'
          ).then((m) => m.FirstTimeLoginComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./pages/auth/reset-password/reset-password.component').then(
            (m) => m.ResetPasswordComponent
          ),
      },
      {
        path: 'forget-password',
        loadComponent: () =>
          import('./pages/auth/forget-password/forget-password.component').then(
            (m) => m.ForgetPasswordComponent
          ),
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'login',
      },
    ],
  },
  {
    path: 'redirect-system',
    loadComponent: () =>
      import('./pages/redirect-system/redirect-system.component').then(
        (m) => m.RedirectSystemComponent
      ),
  },
];
